import { useState, useEffect } from 'react';
import { Check, Loader2, SearchX } from 'lucide-react';
import { ProviderSearchResult } from '@/feature/patients/types';
import { InfiniteScroll } from '@/components/InfiniteScroll';

type SuggestedProvidersProps = {
  searchResults: ProviderSearchResult[] | undefined;
  isLoading: boolean;
  hasMoreResults: boolean;
  onLoadMore: () => void;
  onSelectProvider: (provider: ProviderSearchResult) => void;
  onUnselectProvider?: () => void;
  hasSearchParams?: boolean;
};

const SuggestedProviderItem = ({
  provider,
  isSelected,
  onSelectProvider,
  onUnselectProvider,
}: {
  provider: ProviderSearchResult;
  isSelected: boolean;
  onSelectProvider: (provider: ProviderSearchResult) => void;
  onUnselectProvider?: () => void;
}) => {
  return (
    <div
      className={`relative cursor-pointer rounded-lg bg-white p-3 transition-all ${
        isSelected
          ? 'border-2 border-black'
          : 'border-input border hover:border-blue-200 hover:bg-blue-50'
      }`}
      onClick={() => {
        if (isSelected && onUnselectProvider) {
          onUnselectProvider();
        } else {
          onSelectProvider(provider);
        }
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (isSelected && onUnselectProvider) {
            onUnselectProvider();
          } else {
            onSelectProvider(provider);
          }
        }
      }}
      aria-label={`${isSelected ? 'Click to unselect' : 'Select'} provider ${provider.providerName || provider.facilityName}`}
    >
      {isSelected && (
        <div className="absolute top-3 right-3 flex size-4 items-center justify-center rounded bg-black">
          <Check className="size-4 text-white" />
        </div>
      )}

      <div className="text-foreground pr-8 text-sm font-medium">
        {provider.providerName || provider.facilityName}
      </div>

      {provider.address?.address1 && (
        <div className="text-muted-foreground mt-1 text-xs">
          {provider.address.address1}
          {provider.address.address2 && `, ${provider.address.address2}`}
        </div>
      )}

      {provider.address && (
        <div className="text-muted-foreground text-xs">
          {provider.address.city && provider.address.state
            ? `${provider.address.city}, ${provider.address.state}`
            : provider.address.city || provider.address.state}
          {provider.address.postalCode && ` ${provider.address.postalCode}`}
        </div>
      )}

      {provider.phoneNumbers && provider.phoneNumbers.length > 0 && (
        <div className="text-muted-foreground mt-1 text-xs">
          📞 {provider.phoneNumbers[0]}
        </div>
      )}

      {provider.specialties && provider.specialties.length > 0 && (
        <div className="text-muted-foreground mt-2 text-xs">
          {provider.specialties.join(', ')}
        </div>
      )}
    </div>
  );
};

export const SuggestedProviders = ({
  searchResults,
  isLoading,
  hasMoreResults,
  onLoadMore,
  onSelectProvider,
  onUnselectProvider,
  hasSearchParams = false,
}: SuggestedProvidersProps) => {
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);

  // Reset selection when clearing form
  useEffect(() => {
    if (!hasSearchParams) {
      setSelectedProvider(null);
    }
  }, [hasSearchParams]);

  const hasResults = searchResults && searchResults.length > 0;
  const hasNoResults =
    hasSearchParams && searchResults && searchResults.length === 0;

  const handleSelectProvider = (provider: ProviderSearchResult) => {
    // Use providerNpi for individual providers, facilityNpi for facilities
    const npi = provider.providerNpi || provider.facilityNpi || '';
    setSelectedProvider(npi);
    onSelectProvider(provider);
  };

  const handleUnselectProvider = () => {
    setSelectedProvider(null);
    onUnselectProvider?.();
  };

  return (
    <div className="bg-muted h-full rounded-lg p-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-foreground text-sm font-medium">
          Suggested Providers
        </h3>
      </div>

      {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loader2 className="mr-2 size-4 animate-spin" />
          <p className="text-muted-foreground text-sm">
            Loading search results...
          </p>
        </div>
      ) : hasResults ? (
        <div className="max-h-140 space-y-2 overflow-y-auto">
          <InfiniteScroll
            isLoading={isLoading}
            hasMore={hasMoreResults}
            next={onLoadMore}
            threshold={0.8}
          >
            {searchResults.map((provider, index) => {
              // Use providerNpi for individual providers, facilityNpi for facilities
              const npi =
                provider.providerNpi ||
                provider.facilityNpi ||
                `provider-${index}`;
              const isSelected = selectedProvider === npi;
              return (
                <SuggestedProviderItem
                  key={npi}
                  provider={provider}
                  isSelected={isSelected}
                  onSelectProvider={handleSelectProvider}
                  onUnselectProvider={handleUnselectProvider}
                />
              );
            })}
            {hasMoreResults && (
              <Loader2 className="my-4 h-8 w-8 animate-spin" />
            )}
          </InfiniteScroll>
        </div>
      ) : hasNoResults ? (
        <div className="flex h-full flex-col items-center justify-center text-center">
          <div className="text-muted-foreground mb-4 flex size-16 items-center justify-center rounded-full bg-neutral-300">
            <SearchX className="text-muted-foreground size-8" />
          </div>
          <p className="text-foreground text-sm font-medium">
            No search results.
          </p>
          <p className="text-muted-foreground mt-1 text-sm">
            Try modifying your search criteria
          </p>
        </div>
      ) : (
        <div className="flex h-full flex-col items-center justify-center text-center">
          <img
            src="/suggested-providers.png"
            alt="Suggested providers illustration"
            className="mb-4 w-full max-w-[200px]"
          />
          <p className="text-muted-foreground text-sm">
            Start typing a Provider, City, or ZIP to see suggested matches here
          </p>
        </div>
      )}
    </div>
  );
};
